// Drop-in replacement for ClientsParse.js: fetches the day's
// flight-assigned clients via the REST proxy, groups them by flightId +
// shortCode (same shape FlightCard consumes), and streams live updates
// over the shared /ws-mobile connection.

import { getCurrentDate } from './utils';
import { acquireBus } from './realtimeBus';

export const getLocationFromUrl = () => {
  const p = new URLSearchParams(window.location.search);
  return p.get('location');
};

export const getAirplaneFromUrl = () => {
  const p = new URLSearchParams(window.location.search);
  return p.get('airplane');
};

// Server already resolves flyer1/flyer2/Position pointers, so processing
// is just passthrough. Kept as a seam in case we need to normalize fields.
const processClient = (client) => client;

const sortTicketsInGroup = (tickets) => tickets;

function groupClients(clients) {
  const grouped = {};
  clients.forEach((client) => {
    const flightId = client.Position?.objectId;
    const code = client.shortCode;
    if (!flightId || !code) return;

    if (!grouped[flightId]) {
      grouped[flightId] = {
        flightId,
        status: client.Position?.status || 'pending',
        groups: {},
      };
    }
    if (!grouped[flightId].groups[code]) {
      grouped[flightId].groups[code] = { shortCode: code, tickets: [] };
    }
    grouped[flightId].groups[code].tickets.push(client);
  });

  return Object.values(grouped).map((flight) => ({
    flightId: flight.flightId,
    status: flight.status,
    groups: Object.values(flight.groups).map((g) => ({
      ...g,
      tickets: sortTicketsInGroup(g.tickets),
    })),
  }));
}

// Keep a per-connection raw client list so we can regroup on each update.
// Grouping every event is O(n) but n is small (at most 5000).
function upsertRaw(current, doc) {
  const idx = current.findIndex((c) => c.objectId === doc.objectId);
  if (idx === -1) return [...current, doc];
  const next = [...current];
  next[idx] = doc;
  return next;
}

function removeRaw(current, objectId) {
  return current.filter((c) => c.objectId !== objectId);
}

async function fetchClientsOnce(location, date) {
  const qs = new URLSearchParams({ locationId: location, date });
  const res = await fetch(`/api/mobile/clients?${qs.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`/api/mobile/clients ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data)) throw new Error('clients: non-array payload');
  return data;
}

export const fetchFlightClients = async (setFlightClients, setLoading, opts = {}) => {
  const location = opts.locationId || getLocationFromUrl();
  const airplaneId = opts.airplaneId || getAirplaneFromUrl();
  if (!location) {
    console.error('Location not provided');
    setLoading && setLoading(false);
    return { subscription: makeNoopSub() };
  }

  let raw = [];
  try {
    const date = await getCurrentDate(location);
    raw = (await fetchClientsOnce(location, date)).map(processClient);
    setFlightClients && setFlightClients(groupClients(raw));
    setLoading && setLoading(false);
  } catch (err) {
    console.error('Error fetching clients:', err);
    setLoading && setLoading(false);
    return { subscription: makeNoopSub() };
  }

  const bus = acquireBus(location, airplaneId);
  const listeners = { open: [], close: [] };
  let isOpen = false;

  bus.on('open', () => { isOpen = true; listeners.open.forEach((fn) => fn()); });
  bus.on('close', () => { isOpen = false; listeners.close.forEach((fn) => fn()); });
  bus.on('clients', ({ event, data }) => {
    if (!data) return;
    if (event === 'delete') {
      raw = removeRaw(raw, data.objectId);
    } else {
      // create / update — only keep if still position-assigned
      if (!data.Position) {
        raw = removeRaw(raw, data.objectId);
      } else {
        raw = upsertRaw(raw, processClient(data));
      }
    }
    setFlightClients && setFlightClients(groupClients(raw));
  });

  return {
    subscription: {
      on: (ev, fn) => {
        if (!listeners[ev]) return;
        listeners[ev].push(fn);
        if (ev === 'open' && isOpen) { try { fn(); } catch {} }
      },
      unsubscribe: () => bus.release(),
    },
  };
};

function makeNoopSub() {
  return { on: () => {}, unsubscribe: () => {} };
}
